import {
  LogOut,
  Moon,
  Sun,
  ChevronDown,
  ChevronRight,
  Archive,
  KeyRound,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
} from "lucide-react";
import logo from "../../assets/Advico logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { NotificationButton } from "../userDashboard/ui";
import { ChatMenu } from "../userDashboard/ui/ChatMenu";
import ArchivedChats from "./ArchivedChats";

export default function Sidebar({
  navItems = [],
  activeTab,
  setActiveTab,
  setActiveUseCase,
  activeUseCase,
  onNavChange,
  darkMode,
  setDarkMode,
  user,
  handleLogout,
  chatHistory = [],
  isSidebarOpen = true,
  onNotificationClick,
  unreadCount = 0,
  onUseCasesClick,
  onPin,
  onRename,
  onDelete,
  onArchive,
  onShare,
  onRenamingChange,
  asideRef,
  // collapsed is fully controlled by parent
  collapsed,
  onCollapsedChange,
}) {
  const [vw, setVw] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setVw(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const isMobile = vw < 768;

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedChatId, setSelectedChatId] = useState(null);
  const settingsRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target))
        setSettingsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const storedUser = user || JSON.parse(localStorage.getItem("user") || "{}");
  const displayName = storedUser?.groupName || "User";
  const displayEmail = storedUser?.email || "user@example.com";
  const avatarLetter = displayEmail.charAt(0).toUpperCase();
  const isUser = JSON.parse(localStorage.getItem("user") || "{}")?.groupName?.toLowerCase() === "user";

  const nl = (label = "") => String(label).trim().toLowerCase();

  const getRoute = useCallback((label) => {
    const l = nl(label);
    if (l === "screen master") return "/admin/screens";
    if (l === "role master") return "/admin/roles";
    if (l === "user management" || l === "user master") return "/admin/users";
    if (l === "workspace mapping") return "/admin/workspace-mapping";
    if (l === "data sources" || l === "datasources") return "/admin/data";
    if (l === "authorization") return "/admin/authorization";
    if (l === "usecase master" || l === "use case master") return "/admin/usecasemaster";
    if (l === "user model group mapping" || l === "user modelgroupmapping") return "/admin/usermodelgroupmapping";
    if (l === "company onboarding" || l === "company master" || l === "companies") return "/admin/companies";
    if (l === "overview" || l === "dashboard") return "/admin";
    if (l === "workspaces" || l === "workspace") return "/admin/workspaces";
    if (l === "use cases") return "/user";
    if (l === "chatbot") return "/user/chat";
    return "";
  }, []);

  const getLabel = (label) => nl(label) === "use cases" ? "New Chat" : label;

  const closeMobile = () => {
    if (isMobile) document.dispatchEvent(new CustomEvent("sidebar-close"));
  };

  const handleNavClick = (item) => {
    if (item.hasChildren) {
      setExpandedItems((p) => ({ ...p, [item.id]: !p[item.id] }));
      return;
    }
    onNavChange?.(activeTab);
    const l = nl(item.label);
    if (l === "screen master") navigate("/admin/screens");
    else if (l === "role master") navigate("/admin/roles");
    else if (l === "user management" || l === "user master") navigate("/admin/users");
    else if (l === "workspace mapping") navigate("/admin/workspace-mapping");
    else if (l === "authorization") navigate("/admin/authorization");
    else if (l === "data sources") navigate("/admin/data");
    else if (l === "user model mapping" || l === "user model group mapping") navigate("/admin/usermodelgroupmapping");
    else if (l === "usecase master" || l === "use case master") navigate("/admin/usecasemaster");
    else if (l === "company onboarding" || l === "company master" || l === "companies") navigate("/admin/companies");
    else if (l === "overview") navigate("/admin");
    else if (l === "workspaces" || l === "workspace") navigate("/admin/workspaces");
    else if (l === "use cases") onUseCasesClick ? onUseCasesClick() : navigate("/user");
    else if (l === "chat") navigate("/user/chat");
    closeMobile();
  };

  const handleChatClick = (conversationId, tokenConsumed, userUsecaseId, useCaseName, modelGroupId, modelGroupName) => {
    setActiveTab?.("chat");
    setSelectedChatId(conversationId);
    navigate("/user/chat", {
      state: { conversationId, tokenConsumed: tokenConsumed ?? 0, userUsecaseId, useCaseName, modelGroupId, modelGroupName },
    });
    closeMobile();
  };

  const visibleItems = navItems.filter((item) => {
    const l = nl(item.label);
    if (l === "chatbot") return false;
    return !(activeUseCase === null && l === "chat");
  });

  if (isMobile && !isSidebarOpen) return null;

  const isCollapsed = isMobile ? false : !!collapsed;

  const navBtnCls = (active) =>
    [
      "w-full flex items-center gap-2.5 rounded-xl transition-colors duration-150 text-sm font-medium outline-none select-none",
      isCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2",
      active
        ? "bg-[#e8edf5] text-[#1d57a5] dark:bg-[#1d57a5]/20 dark:text-[#4a8ce3]"
        : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white",
    ].join(" ");

  return (
    <>
      {isMobile && isSidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/50" onClick={closeMobile} />
      )}

      <aside
        ref={asideRef}
        className={[
          "flex flex-col bg-white dark:bg-slate-800",
          "border-r border-slate-200 dark:border-slate-700",
          "flex-shrink-0",
          isMobile
            ? "fixed inset-y-0 left-0 z-40 shadow-2xl w-64"
            : "relative z-20 h-full w-full",
        ].join(" ")}
      >
        {/* ── Logo row ── */}
        <div className="h-14 flex items-center border-b border-slate-100 dark:border-slate-700 px-2 flex-shrink-0 gap-2">
          {!isCollapsed && (
            <div className="flex-1 min-w-0 pl-1">
              <img src={logo} alt="Logo" className="h-9 w-auto max-w-[110px] object-contain" />
            </div>
          )}
          <button
            onClick={() => onCollapsedChange?.(!isCollapsed)}
            className={[
              "p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white",
              "hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0",
              isCollapsed ? "mx-auto" : "ml-auto",
            ].join(" ")}
            title={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>

        {/* ── Nav + history ── */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <nav className="py-2 px-2 space-y-0.5">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const route = getRoute(item.label);
              const isActive =
                route &&
                (route === "/admin"
                  ? currentPath === "/admin" || currentPath === "/admin/"
                  : currentPath.startsWith(route));

              return (
                <div key={item.id}>
                  {item.hasChildren ? (
                    <>
                      {!isCollapsed && (
                        <div className="px-3 pt-3 pb-1">
                          <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                            {item.label}
                          </span>
                        </div>
                      )}
                      <div className="space-y-0.5">
                        {(item.children || [])
                          .filter((c) => nl(c.label) !== "chatbot")
                          .map((child) => {
                            const ChildIcon = child.icon;
                            const childRoute = getRoute(child.label);
                            const childActive =
                              childRoute &&
                              (childRoute === "/admin"
                                ? currentPath === "/admin" || currentPath === "/admin/"
                                : currentPath.startsWith(childRoute));
                            return (
                              <button
                                key={child.id}
                                onClick={() => handleNavClick(child)}
                                title={isCollapsed ? getLabel(child.label) : undefined}
                                className={navBtnCls(childActive)}
                              >
                                <ChildIcon size={16} className="flex-shrink-0" />
                                {!isCollapsed && (
                                  <>
                                    <span className="flex-1 text-left truncate">
                                      {getLabel(child.label)}
                                    </span>
                                    {child.hasChildren &&
                                      (expandedItems[child.id]
                                        ? <ChevronDown size={13} className="flex-shrink-0" />
                                        : <ChevronRight size={13} className="flex-shrink-0" />)}
                                  </>
                                )}
                              </button>
                            );
                          })}
                      </div>
                    </>
                  ) : (
                    <button
                      onClick={() => handleNavClick(item)}
                      title={isCollapsed ? getLabel(item.label) : undefined}
                      className={navBtnCls(isActive)}
                    >
                      <Icon size={17} className="flex-shrink-0" />
                      {!isCollapsed && (
                        <span className="flex-1 text-left truncate">
                          {getLabel(item.label)}
                        </span>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Chat history */}
          {!isCollapsed && chatHistory.length > 0 && (
            <div className="px-2 pb-3 pt-1">
              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2 mb-1.5">
                Recents
              </p>
              <div className="space-y-0.5">
                {chatHistory
                  .filter((x) => !x.isArchived && !x.IsArchived && !x.isArchive)
                  .sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0))
                  .map((chat) => (
                    <button
                      key={chat.conversationId || chat.ConversationId}
                      className={`group relative flex items-center gap-1 w-full px-2 py-1.5 rounded-xl transition-colors cursor-pointer text-left ${
                        selectedChatId === chat.conversationId || selectedChatId === chat.ConversationId
                          ? "bg-slate-200 dark:bg-slate-600"
                          : "hover:bg-slate-100 dark:hover:bg-slate-700"
                      }`}
                      onClick={() =>
                        handleChatClick(
                          chat.conversationId || chat.ConversationId,
                          chat.tokenConsumed,
                          chat.userUsecaseId,
                          chat.useCaseName,
                          chat.modelGroupId,
                          chat.modelGroupName,
                        )
                      }
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate group-hover:text-[#1d57a5] dark:group-hover:text-[#4a8ce3]">
                          {chat.title || chat.Title || "Untitled"}
                        </p>
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">
                          {chat.useCaseName || "General"} · {new Date(chat.lastUpdatedAt).toLocaleDateString()}
                        </p>
                      </div>
                      {chat.isPinned && (
                        <Pin size={11} className="flex-shrink-0 text-[#1d57a5] dark:text-[#4a8ce3]" />
                      )}
                      <ChatMenu
                        chat={chat}
                        onShare={(c) => onShare?.(c.conversationId || c.ConversationId)}
                        onRename={(id, name) => onRename?.(id, name)}
                        onPin={(id) => onPin?.(id, chat.isPinned)}
                        onArchive={(id) => onArchive?.(id)}
                        onDelete={(id) => onDelete?.(id)}
                        onRenamingChange={(v) => { setIsRenaming(v); onRenamingChange?.(v); }}
                      />
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-slate-100 dark:border-slate-700 flex-shrink-0">
          <div className={`flex items-center px-3 py-2 ${isCollapsed ? "flex-col gap-2 justify-center" : "justify-between"}`}>
            {!isUser && (<div onClick={onNotificationClick} className="cursor-pointer">
              <NotificationButton unreadCount={unreadCount} />
            </div>)}
            {!isCollapsed && (
              <button
                onClick={() => setDarkMode?.(!darkMode)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ${darkMode ? "bg-[#1d57a5]" : "bg-slate-300"}`}
                role="switch"
                aria-checked={darkMode}
                title={darkMode ? "Light mode" : "Dark mode"}
              >
                <span
                  className={`absolute left-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm transition-transform duration-300 ${darkMode ? "translate-x-5" : "translate-x-0"}`}
                >
                  {darkMode
                    ? <Moon size={11} className="text-[#1d57a5]" />
                    : <Sun size={11} className="text-amber-500" />}
                </span>
              </button>
            )}
          </div>

          <div className={`flex items-center gap-2 px-2 py-2 ${isCollapsed ? "flex-col" : ""}`}>
            <div className="relative flex-shrink-0" ref={settingsRef}>
              <button
                onClick={() => setSettingsOpen((p) => !p)}
                className="w-8 h-8 rounded-full bg-[#1d57a5] text-white flex items-center justify-center font-bold text-sm shadow-sm hover:bg-[#164280] transition-colors"
                title={isCollapsed ? `${displayName} · ${displayEmail}` : undefined}
              >
                {avatarLetter}
              </button>
              {settingsOpen && (
                <div className="absolute bottom-10 left-0 w-52 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-[9999] overflow-hidden">
                  <div className="px-3 py-2.5 border-b border-slate-100 dark:border-slate-700">
                    <p className="text-xs font-bold text-slate-800 dark:text-white truncate">{displayName}</p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{displayEmail}</p>
                  </div>
                  <button
                    onClick={() => { setSettingsOpen(false); navigate("/admin/change-password"); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <KeyRound size={14} className="text-slate-400" /> Change Password
                  </button>
                  {isUser && (
                    <button
                      onClick={() => { setSettingsOpen(false); setArchivedOpen(true); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <Archive size={14} className="text-slate-400" /> Archive Chat
                    </button>
                  )}
                  <div className="mx-3 border-t border-slate-100 dark:border-slate-700" />
                  <button
                    onClick={() => { setSettingsOpen(false); handleLogout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut size={14} /> Logout
                  </button>
                </div>
              )}
            </div>

            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-800 dark:text-white truncate">{displayName}</p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{displayEmail}</p>
              </div>
            )}

            <button
              onClick={handleLogout}
              className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded-lg flex-shrink-0"
              title="Logout"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>

        <ArchivedChats
          isOpen={archivedOpen}
          onClose={() => setArchivedOpen(false)}
          archivedChats={chatHistory.filter((x) => x.isArchived || x.IsArchived || x.isArchive)}
          onUnarchive={(id) => onArchive?.(id, false)}
          onDelete={(id) => onDelete?.(id)}
        />
      </aside>
    </>
  );
}
